import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi, formatApiError, resolveImageUrl } from "../../lib/adminApi";
import { Plus, Pencil, Trash2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function AdminAlliesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reBusy, setReBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/allies");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (a) => {
    if (!window.confirm(`¿Eliminar el aliado "${a.name}"?`)) return;
    try {
      await adminApi.delete(`/allies/${a.id}`);
      toast.success("Aliado eliminado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const reprocessAll = async () => {
    if (!window.confirm("Esto re-procesará todos los logos quitando el fondo de cada uno (blanco, negro o color uniforme). ¿Continuar?")) return;
    setReBusy(true);
    try {
      const { data } = await adminApi.post("/admin/reprocess-allies");
      toast.success(`Re-procesados: ${data.reprocessed} de ${data.scanned}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReBusy(false);
    }
  };

  return (
    <section className="px-10 py-12" data-testid="admin-allies-list">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <div
            className="text-[var(--pc-bronze)]"
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 400,
              fontSize: 11,
              letterSpacing: "0.34em",
              textTransform: "uppercase",
            }}
          >
            Marcas
          </div>
          <h1
            className="headline-xl mt-3 text-[var(--pc-text)]"
            style={{ fontSize: "clamp(26px, 2.6vw, 38px)", letterSpacing: "0.16em", lineHeight: 1.2 }}
          >
            Aliados
          </h1>
          <div className="h-px w-10 bg-[var(--pc-bronze)] mt-5 opacity-90" />
          <p className="body-soft mt-6 max-w-[540px]" style={{ fontSize: 14 }}>
            Logos de las marcas con las que has diseñado. Aparecen en la portada
            justo después de la sección de Inversión blindada.
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={reprocessAll}
            disabled={reBusy}
            className="admin-btn-ghost flex items-center gap-2"
            data-testid="reprocess-allies-btn"
            title="Quitar fondo de los logos ya cargados"
          >
            {reBusy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            <span>{reBusy ? "Procesando..." : "Reprocesar logos"}</span>
          </button>
          <Link to="/admin/aliados/nuevo" className="admin-btn-primary" data-testid="new-ally-btn">
            <Plus size={14} className="inline mr-2" />
            Nuevo aliado
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="py-20 flex justify-center">
          <Loader2 className="text-[var(--pc-bronze)] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="mt-12 text-[var(--pc-text-soft)]" style={{ fontFamily: "Inter", fontSize: 13 }}>
          Aún no hay aliados. Crea el primero con el botón &quot;Nuevo aliado&quot;.
        </p>
      ) : (
        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((a) => (
            <div
              key={a.id}
              className="border border-[var(--pc-line)] bg-[#0b0b0d]"
              data-testid={`ally-card-${a.id}`}
            >
              <div
                className="relative flex items-center justify-center bg-[#0e0e11] p-8"
                style={{ aspectRatio: "16/10" }}
              >
                {a.logo ? (
                  <img
                    src={resolveImageUrl(a.logo)}
                    alt={a.name}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : (
                  <div className="text-[var(--pc-text-soft)] text-xs">Sin logo</div>
                )}
              </div>
              <div className="p-5">
                <h3
                  className="text-[var(--pc-text)]"
                  style={{
                    fontFamily: "Inter, sans-serif",
                    fontWeight: 300,
                    fontSize: 15,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                  }}
                >
                  {a.name}
                </h3>
                {a.url && (
                  <a
                    href={a.url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-[var(--pc-text-mute)] hover:text-[var(--pc-bronze-bright)] block mt-1 truncate"
                    style={{ fontFamily: "Inter", fontSize: 11, letterSpacing: "0.04em" }}
                  >
                    {a.url}
                  </a>
                )}
                <div className="flex flex-wrap gap-2 mt-5">
                  <Link
                    to={`/admin/aliados/${a.id}`}
                    className="admin-btn-ghost flex items-center gap-2 text-xs"
                    data-testid={`edit-ally-${a.id}`}
                  >
                    <Pencil size={12} /> Editar
                  </Link>
                  <button
                    onClick={() => onDelete(a)}
                    className="admin-btn-ghost flex items-center gap-2 text-xs text-red-300 hover:text-red-200"
                    data-testid={`delete-ally-${a.id}`}
                  >
                    <Trash2 size={12} /> Eliminar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
